import { useParams } from 'react-router-dom';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { Tabs, TabsStrategy, TabsVariant } from '../../components/Tabs';
import { Participations } from '../../components/Participations';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useIdParam } from '../../utils/use-id-param.util';
import {
  PATH_ORGANIZATION_ADDRESSES,
  PATH_ORGANIZATION_ADDRESSES_EDIT,
  PATH_ORGANIZATION_ADDRESSES_EDIT_NEW,
  PATH_ORGANIZATION_ADDRESSES_EDIT_MODIFY,
  PATH_ORGANIZATION_ADDRESSES_EDIT_DELETE,
  PATH_ORGANIZATION_CONTACT,
  PATH_ORGANIZATION_CONTACT_EDIT,
  PATH_ORGANIZATION_GENERAL,
  PATH_ORGANIZATION_GENERAL_EDIT,
  PATH_ORGANIZATION_BILLING,
  PATH_ORGANIZATION_BILLING_EDIT,
  PATH_ORGANIZATION_BANK,
  PATH_ORGANIZATION_BANK_EDIT,
  PATH_ORGANIZATION_MEMBERSHIPS,
  PATH_ORGANIZATION_MEMBERSHIPS_EDIT,
  PATH_ORGANIZATION_DIRECTORY,
  PATH_ORGANIZATION_VERSIONS,
  PATH_ORGANIZATION_ADMIN,
  PATH_ORGANIZATION_ADMIN_EDIT,
  PATH_ORGANIZATION_DEFAULT,
  PATH_ORGANIZATION_PARTICIPATIONS,
  PATH_ORGANIZATION_PENNYLANE,
  PATH_ORGANIZATION_RELATIONS,
  PATH_ORGANIZATION_RELATIONS_EDIT,
} from '../../app/constants/paths.constant';

import { Admin } from './Admin';
import { GeneralInfo } from './GeneralInfo';
import { Directory } from './Directory';
import { Versions } from './Versions';
import { Memberships } from './Memberships';
import { Relations } from './Relations';
import { ContactInfo } from './ContactInfo';
import { Addresses } from './Addresses';
import { Billing } from './Billing';
import { BankInfo } from './BankInfo';
import { Pennylane } from './Pennylane';

export const OrganizationPage_Tabs_Organization_Fragment = graphql(`
  fragment OrganizationPage_Tabs_Organization_Fragment on Organization {
    _id
    ...OrganizationPage_GeneralInfo_Organization_Fragment
    ...OrganizationPage_Memberships_Organization_Fragment
    ...OrganizationPage_Addresses_Organization_Fragment
    ...OrganizationPage_ContactInfo_Organization_Fragment
    ...OrganizationPage_BankInfo_Organization_Fragment
    ...OrganizationPage_Relations_Organization_Fragment
    ...OrganizationPage_Billing_Organization_Fragment
    ...OrganizationPage_Admin_Organization_Fragment
    ...OrganizationPage_Pennylane_Organization_Fragment
    ...OrganizationPage_Directory_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_Tabs_Organization_Fragment
  >;
  readonly variant?: TabsVariant;
}

export const OrganizationTabs = ({
  organization: pOrganization,
  variant,
}: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_Tabs_Organization_Fragment,
    pOrganization,
  );

  const id = useIdParam();
  const { addressId } = useParams();

  const { paths, getActiveTab } = useBuildPaths(
    {
      addresses: PATH_ORGANIZATION_ADDRESSES,
      addressesEdit: PATH_ORGANIZATION_ADDRESSES_EDIT,
      addressesEditDelete: PATH_ORGANIZATION_ADDRESSES_EDIT_DELETE,
      addressesEditNew: PATH_ORGANIZATION_ADDRESSES_EDIT_NEW,
      addressesEditModify: PATH_ORGANIZATION_ADDRESSES_EDIT_MODIFY,
      admin: PATH_ORGANIZATION_ADMIN,
      adminEdit: PATH_ORGANIZATION_ADMIN_EDIT,
      billing: PATH_ORGANIZATION_BILLING,
      billingEdit: PATH_ORGANIZATION_BILLING_EDIT,
      bankInfo: PATH_ORGANIZATION_BANK,
      bankInfoEdit: PATH_ORGANIZATION_BANK_EDIT,
      contactInfo: PATH_ORGANIZATION_CONTACT,
      contactInfoEdit: PATH_ORGANIZATION_CONTACT_EDIT,
      default: PATH_ORGANIZATION_DEFAULT,
      directory: PATH_ORGANIZATION_DIRECTORY,
      generalInfo: PATH_ORGANIZATION_GENERAL,
      generalInfoEdit: PATH_ORGANIZATION_GENERAL_EDIT,
      memberships: PATH_ORGANIZATION_MEMBERSHIPS,
      membershipsEdit: PATH_ORGANIZATION_MEMBERSHIPS_EDIT,
      participations: PATH_ORGANIZATION_PARTICIPATIONS,
      pennylane: PATH_ORGANIZATION_PENNYLANE,
      relations: PATH_ORGANIZATION_RELATIONS,
      relationsEdit: PATH_ORGANIZATION_RELATIONS_EDIT,
      versions: PATH_ORGANIZATION_VERSIONS,
    },
    {
      id,
      addressId,
    },
    {
      addresses: 'addresses',
      addressesEdit: 'addresses',
      addressesEditDelete: 'addresses',
      addressesEditNew: 'addresses',
      addressesEditModify: 'addresses',
      admin: 'admin',
      adminEdit: 'admin',
      bankInfo: 'bankInfo',
      bankInfoEdit: 'bankInfo',
      billing: 'billing',
      billingEdit: 'billing',
      contactInfo: 'contactInfo',
      contactInfoEdit: 'contactInfo',
      default: 'generalInfo',
      directory: 'directory',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      memberships: 'memberships',
      membershipsEdit: 'memberships',
      participations: 'participations',
      pennylane: 'pennylane',
      relations: 'relations',
      relationsEdit: 'relations',
      versions: 'versions',
    },
  );

  const addressesPaths =
    paths.addresses &&
    paths.addressesEdit &&
    paths.addressesEditNew &&
    paths.addressesEditModify &&
    paths.addressesEditDelete
      ? {
          consult: paths.addresses,
          edit: paths.addressesEdit,
          editNew: paths.addressesEditNew,
          editModify: paths.addressesEditModify,
          editDelete: paths.addressesEditDelete,
        }
      : undefined;

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="organization-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo organization={organization} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'contactInfo',
          label: 'Contact',
          panel: <ContactInfo organization={organization} />,
          to: paths.contactInfo,
          value: 'contactInfo',
        },
        {
          key: 'addresses',
          label: 'Adresses',
          panel: (
            <Addresses organization={organization} paths={addressesPaths} />
          ),
          to: paths.addresses,
          value: 'addresses',
        },
        {
          key: 'billing',
          label: 'Facturation',
          panel: <Billing organization={organization} />,
          to: paths.billing,
          value: 'billing',
        },
        {
          key: 'bankInfo',
          label: 'Bancaire',
          panel: <BankInfo organization={organization} />,
          to: paths.bankInfo,
          value: 'bankInfo',
        },
        {
          key: 'memberships',
          label: 'Membres',
          panel: <Memberships organization={organization} />,
          to: paths.memberships,
          value: 'memberships',
        },
        {
          key: 'relations',
          label: 'Relations',
          panel: <Relations organization={organization} />,
          to: paths.relations,
          value: 'relations',
        },
        {
          key: 'participations',
          label: 'Missions Liées',
          panel: <Participations thirdPartyId={organization._id} />,
          to: paths.participations,
          value: 'participations',
        },
        {
          key: 'pennylane',
          label: 'Pennylane',
          panel: <Pennylane organization={organization} />,
          to: paths.pennylane,
          value: 'pennylane',
        },
        {
          key: 'directory',
          label: 'Annuaire',
          panel: <Directory organization={organization} />,
          to: paths.directory,
          value: 'directory',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions originalId={organization._id} />,
          to: paths.versions,
          value: 'versions',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: <Admin organization={organization} />,
          to: paths.admin,
          value: 'admin',
        },
      ].filter(
        (tab) => Object.keys(paths).findIndex((key) => key === tab.key) > -1,
      )}
      title="Onglets de l'écran de Fiche d'une Organisation"
      variant={variant ?? TabsVariant.Horizontal}
    />
  );
};
